/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect } from 'react';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import * as R from 'ramda';

// Material imports
import { Box, Divider, TextField, Typography } from '@mui/material';

// Components
import { ErrorBoundary } from 'common';
import { validateIntGTZero } from 'utils/helpers/formValidations';
import RadioGroup from './radio-group/RadioGroup';
import { LocationListSection, ToggleSection } from './sections/Sections';
import ReconsignmentBanner from '../reconsignments-banner/ReconsignmentBanner';

export const Fulfillment = ({
  balances,
  data,
  setValue,
  restrictedLocations,
  setAllowContinue,
  volume,
  setVolumeValidation,
  volumeValidation,
}) => {
  const {
    uncontrolledAvailableInventory,
    uncontrolledInTransitInventory,
    anyUncontrolledInventory = {},
    anyAvailableInventory,
    anyInTransitInventory,
    anyInventory,
  } = balances;

  // if We have restricted locations AND toggle is switched to yes -> restricted flow
  const restrictedFlow = !!(data.setReconsignmentControls && restrictedLocations.length);

  // if restricted flow we update redux with Uncontrol reconsignment , otherwise set to Any inventory (EITHER)
  let reconsignmentControl = restrictedFlow ? 'UNCONTROL' : 'EITHER';
  reconsignmentControl =
    data.setReconsignmentControls === false ? data.reconsignmentControl : reconsignmentControl;

  const setVolume = val => setValue('volume', val);

  const handleUpdateVolume = R.compose(
    setVolumeValidation,
    validateIntGTZero,
    R.replace(/,/g, ''),
    R.tap(setVolume),
    x => x.toLocaleString(),
    R.unless(R.isEmpty, parseInt),
    R.replace(/^0+|\D/g, ''),
    R.path(['target', 'value'])
  );

  useEffect(() => {
    setValue('reconsignmentControl', reconsignmentControl);
    setValue('convertBarrels', restrictedFlow);
  }, [reconsignmentControl, restrictedFlow, setValue]);

  // restricted Inventory === uncontrolled Inventory, meaning that it CAN be restricted. Controlled inventory has already been restricted and cannot be UNrestricted
  const restrictedInventory = {
    anyUncontrolledInventory,
    uncontrolledAvailableInventory,
    uncontrolledInTransitInventory,
  };

  // Uncontrolled or All Inventory to show depending on the available inventory and reconsigmnet control (toggled by yes/no switch) selection
  let inventoryOptions = restrictedFlow ? restrictedInventory : balances;
  const companyIsMpl = data.dataCompanyId === 1;

  // Don't show controlled and uncontrolled inventory if dataCompany is not MPL
  inventoryOptions = companyIsMpl
    ? inventoryOptions
    : { anyAvailableInventory, anyInTransitInventory, anyInventory };

  // Show toggle section ONLY if we have restricted locations, company is MPL, and there is uncontrolled inventory
  const showToggle = !!(
    anyUncontrolledInventory.balance &&
    companyIsMpl &&
    restrictedLocations.length
  );

  useEffect(() => {
    if (R.prop('isFutureDate', data)) {
      setAllowContinue(true);
      return;
    }
    const availableBalances = restrictedFlow
      ? inventoryOptions.anyUncontrolledInventory.balance
      : inventoryOptions.anyInventory.balance;

    // returns true if some restricted locations do not have origins and restricted flow
    const locationHasNoOrigins =
      restrictedFlow && restrictedLocations.some(({ locations }) => !locations.length);

    // disallow continue if requested balance exceeds availableBalance or locationHasNoOrigins is true
    R.ifElse(
      R.either(R.lt(availableBalances), () => locationHasNoOrigins),
      () => setAllowContinue(false),
      () => setAllowContinue(true)
    )(parseInt(R.replace(/,/g, '', volume ?? '')));
  }, [data, inventoryOptions, restrictedFlow, restrictedLocations, setAllowContinue, volume]);

  const createFulfillmentOptions = () => {
    return Object.keys(inventoryOptions).map(option => ({
      classNames: 'css-heap-redact',
      key: inventoryOptions[option].key,
      label: inventoryOptions[option].title,
      value: inventoryOptions[option],
    }));
  };
  return (
    <Box display="flex" flexDirection="column" p={3} pb={8} w={1}>
      {showToggle && <ToggleSection reconsignment={restrictedFlow} setValue={setValue} />}
      {!!restrictedFlow && <ReconsignmentBanner />}
      {restrictedFlow && companyIsMpl ? (
        <LocationListSection restrictedLocations={restrictedLocations} />
      ) : (
        <>
          <Divider sx={{ display: showToggle ? 'block' : 'none', mx: 0, my: 2 }} />
          <Typography variant="h7">How would you like to fulfill this order?</Typography>
        </>
      )}
      <TextField
        error={!R.isEmpty(volumeValidation)}
        helperText={volumeValidation}
        id="volume"
        label="Volume (bbls)"
        onChange={handleUpdateVolume}
        sx={{ mt: 2 }}
        value={volume || ''}
      />
      {R.prop('isFutureDate', data) || Object.keys(balances).length ? (
        <RadioGroup
          inventoryOptions={createFulfillmentOptions()}
          inventorySelection={data.inventorySelection}
          isFutureDate={data.isFutureDate}
          reconsignmentControl={reconsignmentControl}
          restrictedFlow={restrictedFlow}
          setValue={setValue}
          volume={R.replace(/,/g, '', volume ?? '')}
        />
      ) : null}
      <Divider sx={{ display: restrictedFlow ? 'block' : 'none', mx: 0, my: 2 }} />
    </Box>
  );
};

const FulfillmentStep = props => (
  <ErrorBoundary>
    <Fulfillment {...props} />
  </ErrorBoundary>
);

export default FulfillmentStep;

Fulfillment.propTypes = {
  balances: shape({
    anyAvailableInventory: shape({
      balance: number,
      inventorySelection: string,
      key: string,
      reconsignmentControl: string,
      title: string,
    }),
    anyControlledInventory: shape({
      balance: number,
      inventorySelection: string,
      key: string,
      reconsignmentControl: string,
      title: string,
    }),
    anyInTransitInventory: shape({
      balance: number,
      inventorySelection: string,
      key: string,
      reconsignmentControl: string,
      title: string,
    }),
    anyInventory: shape({
      balance: number,
      inventorySelection: string,
      key: string,
      reconsignmentControl: string,
      title: string,
    }),
    anyUncontrolledInventory: shape({
      balance: number,
      inventorySelection: string,
      key: string,
      reconsignmentControl: string,
      title: string,
    }),
    controlledAvailableInventory: shape({
      balance: number,
      inventorySelection: string,
      key: string,
      reconsignmentControl: string,
      title: string,
    }),
    controlledInTransitInventory: shape({
      balance: number,
      inventorySelection: string,
      key: string,
      reconsignmentControl: string,
      title: string,
    }),
    uncontrolledAvailableInventory: shape({
      balance: number,
      inventorySelection: string,
      key: string,
      reconsignmentControl: string,
      title: string,
    }),
    uncontrolledInTransitInventory: shape({
      balance: number,
      inventorySelection: string,
      key: string,
      reconsignmentControl: string,
      title: string,
    }),
  }).isRequired,
  data: shape({
    convertBarrels: bool,
    inventorySelection: string,
    isFutureDate: bool,
    reconsignmentControl: string,
    volume: string,
  }).isRequired,
  restrictedLocations: arrayOf(
    shape({
      id: number,
      locations: arrayOf(shape({ id: number, nameLong: string, nameShort: string })),
      nameLong: string,
      nameShort: string,
    })
  ),
  setAllowContinue: func.isRequired,
  setValue: func.isRequired,
  setVolumeValidation: func.isRequired,
  volume: string.isRequired,
  volumeValidation: string.isRequired,
};

Fulfillment.defaultProps = {
  restrictedLocations: [],
};
